'use client';

import type { ChatHeaderProps } from './types';
import { CloseIcon } from '../search/icons';
import { useClientConfig } from '@/lib/clients/ClientContext';

export default function ChatHeader({ onClose, onSwitchToSearch }: ChatHeaderProps) {
  const clientConfig = useClientConfig();
  // Both default to today's exact treatment ("CLOSE CHAT", black), so a client
  // that sets neither renders byte-identically to before. Only syfak sets them.
  const backLabel = clientConfig.ui?.chatBackLabel ?? 'CLOSE CHAT';
  // `bg-brand`/`hover:bg-brand-hover` resolve to the client's OWN brandColor via
  // the --color-brand custom properties, so the colour comes from that client's
  // config rather than a value hardcoded here. Both classes already exist in
  // widget.css AND globals.css — the embed's stylesheet is a hand-maintained
  // allowlist, so a class missing from it silently does nothing (the trap that
  // froze the offer-popup spinner, §5.13).
  // The TEXT colour has to travel with the background, not sit beside it.
  // White is correct on black and stays. On the brand colour it is not
  // necessarily correct: a pale brand (kostaboda's #74CCD3 is 1.86:1 against
  // white) would render this label invisible. So the brand variant defers to
  // `--color-brand-text`, which defaults to white — a no-op for syfak, the
  // only client setting chatBackTone today.
  const backTone =
    clientConfig.ui?.chatBackTone === 'brand'
      ? 'bg-brand hover:bg-brand-hover text-brand-text'
      : 'bg-black hover:bg-[#333] text-white';

  return (
    <div className="flex items-center justify-between self-stretch pt-6 pb-3 px-6 flex-shrink-0 border-b border-[#E5E7EB]">
      <button
        onClick={onSwitchToSearch}
        className={`flex items-center justify-center gap-2.5 p-2.5 ${backTone} transition-colors text-[12px] font-gotham font-medium cursor-pointer`}
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
          <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {backLabel}
      </button>

      <button
        onClick={onClose}
        className="w-9 h-9 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
        aria-label="Close"
      >
        <CloseIcon className="w-5 h-5" />
      </button>
    </div>
  );
}
